import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { UserPlus } from '@phosphor-icons/react';

/** Turns a one-off quote/invoice customer into a full client with portal access. */
export const ConvertToClientDialog = ({ open, onOpenChange, doc, onConverted }) => {
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', address: '', vat_number: '', registration_number: '',
  });
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !doc) return;
    setResult(null);
    setForm({
      company_name: doc.client_name || '',
      contact_name: doc.client_name || '',
      email: doc.client_email || '',
      phone: doc.client_phone || '',
      address: doc.client_address || '',
      vat_number: doc.client_vat_number || '',
      registration_number: '',
    });
  }, [open, doc]);

  const submit = async () => {
    if (!form.company_name.trim()) { toast.error('Enter the customer/company name'); return; }
    if (!form.email.trim()) { toast.error('An email address is needed for portal access'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/customers/convert-to-client', {
        quotation_id: doc?.quotation_id || null,
        invoice_id: doc?.invoice_id || null,
        ...form,
        send_welcome: sendWelcome,
      });
      setResult(data);
      toast.success(data.message);
      onConverted?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Turn into a full client</DialogTitle>
          <DialogDescription>
            Creates the company, the primary contact and a client portal login, then moves this customer's
            quotes and invoices onto the new account.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 mt-2" data-testid="convert-result">
            <div className="border-l-4 border-[#4CAF50] bg-[#4CAF50]/5 p-4 space-y-1 text-sm">
              <p className="font-semibold text-[#0A192F]">{result.message}</p>
              <p className="text-gray-600">Portal email: <strong>{result.email}</strong></p>
              {result.temporary_password && (
                <p className="text-gray-600">
                  Temporary password: <strong className="font-mono">{result.temporary_password}</strong>
                  {result.welcome_email_sent ? ' (emailed to the client)' : ' (share this with the client)'}
                </p>
              )}
              <p className="text-gray-600">
                Moved across: {result.relinked_quotations} quotation(s), {result.relinked_invoices} invoice(s)
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => onOpenChange(false)} className="rounded-none bg-[#0A192F] hover:bg-[#0A192F]/90" data-testid="convert-done-btn">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Company / customer name *</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-none mt-1" data-testid="convert-company-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Primary contact name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none mt-1" data-testid="convert-contact-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Email (portal login) *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="convert-email" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" data-testid="convert-phone" />
              </div>
              <div>
                <Label className="text-sm font-semibold">VAT number</Label>
                <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="convert-vat" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Company reg. number</Label>
                <Input value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="convert-reg" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none mt-1" data-testid="convert-address" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={sendWelcome} onChange={(e) => setSendWelcome(e.target.checked)} className="accent-[#4CAF50]" data-testid="convert-send-welcome" />
              Email the client their portal login details
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="convert-submit-btn">
                <UserPlus className="w-4 h-4 mr-2" /> {busy ? 'Creating…' : 'Create Client Account'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConvertToClientDialog;
